import { Injectable } from '@angular/core';

import { AuthTokensDto } from '../../../features/auth/data-access/auth.dto';

const accessTokenStorageKey = 'qarqyn.auth.access-token';
const refreshTokenStorageKey = 'qarqyn.auth.refresh-token';

interface JwtPayload {
  exp?: number;
}

@Injectable({
  providedIn: 'root',
})
export class TokenService {
  hasAccessToken(): boolean {
    return !!this.getAccessToken();
  }

  hasRefreshToken(): boolean {
    return !!this.getRefreshToken();
  }

  getAccessToken(): string | null {
    if (typeof localStorage === 'undefined') {
      return null;
    }

    return localStorage.getItem(accessTokenStorageKey);
  }

  getRefreshToken(): string | null {
    if (typeof localStorage === 'undefined') {
      return null;
    }

    return localStorage.getItem(refreshTokenStorageKey);
  }

  setTokens(tokens: AuthTokensDto): void {
    if (typeof localStorage === 'undefined') {
      return;
    }

    localStorage.setItem(accessTokenStorageKey, tokens.access_token);
    localStorage.setItem(refreshTokenStorageKey, tokens.refresh_token);
  }

  clearTokens(): void {
    if (typeof localStorage === 'undefined') {
      return;
    }

    localStorage.removeItem(accessTokenStorageKey);
    localStorage.removeItem(refreshTokenStorageKey);
  }

  isAccessTokenExpiringSoon(bufferInSeconds = 60): boolean {
    const accessToken = this.getAccessToken();

    if (!accessToken) {
      return false;
    }

    return this.isTokenExpired(accessToken, bufferInSeconds);
  }

  private isTokenExpired(token: string, bufferInSeconds = 0): boolean {
    const payload = this.parseJwtPayload(token);

    if (!payload?.exp) {
      return false;
    }

    const nowInSeconds = Math.floor(Date.now() / 1000);
    return payload.exp <= nowInSeconds + bufferInSeconds;
  }

  private parseJwtPayload(token: string): JwtPayload | null {
    try {
      const payload = token.split('.')[1];

      if (!payload) {
        return null;
      }

      const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
      const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');

      return JSON.parse(atob(padded)) as JwtPayload;
    } catch {
      return null;
    }
  }
}
